/**
 * The SVG subset the print server accepts, and the sanitiser that gets our pin
 * art into it.
 *
 * The print server draws client-supplied symbols itself, into a vector PDF, so
 * it implements only a restricted subset of SVG: the elements below, colours as
 * `#rgb` / `#rrggbb` / `none`, and no arc path commands. Validation there is
 * strict and rejects the **whole print job**, not the offending symbol — one bad
 * pin means no PDF at all.
 *
 * Our art does not pass as authored. Of the 115 `map_pin_*.svg` assets, 112 wrap
 * their artwork in a `<defs><filter>` drop shadow, 33 use arc commands, and 11
 * use a `<mask>`. Drop shadows are removed for print. Outside-stroke masks are
 * reproduced by placing the stroke beneath its fill, and arcs become cubic
 * Béziers. Rectangle masks are dropped only for assets whose content has been
 * checked to remain inside the rectangle.
 *
 * This runs at print time rather than as a build step so that a newly added pin
 * cannot fall out of sync with a generated copy. `printSvgSubset.assets.test.ts`
 * sweeps every real asset through it, so a pin whose art cannot be sanitised
 * fails the test suite instead of silently breaking printing months later.
 */

import { nonZeroPathFromEvenOdd } from './printPathWinding'

/** Elements the print server draws. Anything else is dropped by the sanitiser. */
const ALLOWED_ELEMENTS = new Set([
  'svg',
  'g',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
])

/**
 * Attributes kept on the sanitised output: geometry, plus the presentation
 * attributes the server documents (fill, stroke, stroke-width, opacity, style,
 * transform).
 *
 * Even-odd paths are rewound to nonzero contours before serialization, because
 * Tanger ignores fill-rule. Masked outside strokes move below their fills.
 */
const ALLOWED_ATTRIBUTES = new Set([
  'viewbox',
  'd',
  'points',
  'x',
  'y',
  'width',
  'height',
  'rx',
  'ry',
  'cx',
  'cy',
  'r',
  'x1',
  'y1',
  'x2',
  'y2',
  'fill',
  'fill-opacity',
  'fill-rule',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-miterlimit',
  'opacity',
  'style',
  'transform',
])

/** Attributes that point at machinery the subset has no equivalent for. */
const EFFECT_ATTRIBUTES = new Set(['filter', 'mask', 'clip-path'])

/** Colour keywords worth translating rather than dropping. */
const NAMED_COLOURS: Record<string, string> = {
  white: '#ffffff',
  black: '#000000',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  gray: '#808080',
  grey: '#808080',
  none: 'none',
  transparent: 'none',
}

const COLOUR_ATTRIBUTES = new Set(['fill', 'stroke'])

const PATH_COMMAND = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g

const parseNumbers = (raw: string): number[] =>
  (raw.match(/-?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g) ?? []).map(Number)

// ── Validation ────────────────────────────────────────────────────────────

function parseSvg(svgText: string): Element | null {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  if (doc.getElementsByTagName('parsererror').length > 0) return null
  return doc.documentElement ?? null
}

/**
 * Everything about `svgText` that the print server would reject, as readable
 * strings. An empty array means the document is inside the subset.
 */
export function printSvgViolations(svgText: string): string[] {
  const root = parseSvg(svgText)
  if (root === null) return ['document is not parseable XML']

  const violations: string[] = []
  const localName = (el: Element) => el.localName ?? el.nodeName

  if (localName(root) !== 'svg') violations.push('root element is not svg')
  else if (!root.hasAttribute('viewBox')) violations.push('svg element is missing a viewBox')

  const visit = (el: Element) => {
    const name = localName(el)
    if (!ALLOWED_ELEMENTS.has(name)) violations.push(`disallowed element: ${name}`)

    for (const attr of Array.from(el.attributes)) {
      const attrName = attr.name.toLowerCase()
      if (EFFECT_ATTRIBUTES.has(attrName)) violations.push(`disallowed attribute: ${attrName}`)
      if (COLOUR_ATTRIBUTES.has(attrName) && !isSupportedColour(attr.value)) {
        violations.push(`unsupported colour value: ${attr.value}`)
      }
    }

    if (name === 'path' && /[Aa]/.test(el.getAttribute('d') ?? '')) {
      violations.push('path data uses arc commands (A/a)')
    }

    for (const child of Array.from(el.children)) visit(child)
  }

  visit(root)
  return [...new Set(violations)]
}

function isSupportedColour(value: string): boolean {
  const v = value.trim().toLowerCase()
  return v === 'none' || /^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(v)
}

// ── Arc → cubic Bézier ────────────────────────────────────────────────────

/**
 * One arc as a list of cubic Bézier segments, each `[c1x, c1y, c2x, c2y, x, y]`.
 *
 * The endpoint-to-centre conversion is the one in the SVG implementation notes;
 * the arc is then split at 90° or finer, because a single cubic can only
 * approximate a quarter turn well. Each segment's control points come from the
 * ellipse's derivative at its ends, which is exact for the tangent direction and
 * accurate to a few parts in ten thousand for the curve between.
 */
function arcToCubics(
  x1: number,
  y1: number,
  rxIn: number,
  ryIn: number,
  rotationDeg: number,
  largeArc: boolean,
  sweep: boolean,
  x2: number,
  y2: number,
): number[][] {
  // A zero radius is a straight line, per the SVG spec.
  if (rxIn === 0 || ryIn === 0) return []

  const phi = (rotationDeg * Math.PI) / 180
  const cosPhi = Math.cos(phi)
  const sinPhi = Math.sin(phi)

  const dx = (x1 - x2) / 2
  const dy = (y1 - y2) / 2
  const x1p = cosPhi * dx + sinPhi * dy
  const y1p = -sinPhi * dx + cosPhi * dy

  let rx = Math.abs(rxIn)
  let ry = Math.abs(ryIn)

  // Radii too small to span the endpoints are scaled up until they just fit.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry)
  if (lambda > 1) {
    const s = Math.sqrt(lambda)
    rx *= s
    ry *= s
  }

  const sign = largeArc === sweep ? -1 : 1
  const numerator = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p
  const denominator = rx * rx * y1p * y1p + ry * ry * x1p * x1p
  const coef = sign * Math.sqrt(Math.max(0, numerator / denominator))

  const cxp = (coef * rx * y1p) / ry
  const cyp = (-coef * ry * x1p) / rx
  const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2
  const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2

  const angle = (ux: number, uy: number, vx: number, vy: number): number => {
    const dot = ux * vx + uy * vy
    const len = Math.hypot(ux, uy) * Math.hypot(vx, vy)
    const a = Math.acos(Math.min(1, Math.max(-1, len === 0 ? 1 : dot / len)))
    return ux * vy - uy * vx < 0 ? -a : a
  }

  const startX = (x1p - cxp) / rx
  const startY = (y1p - cyp) / ry
  const endX = (-x1p - cxp) / rx
  const endY = (-y1p - cyp) / ry

  const theta1 = angle(1, 0, startX, startY)
  let deltaTheta = angle(startX, startY, endX, endY)
  if (!sweep && deltaTheta > 0) deltaTheta -= 2 * Math.PI
  if (sweep && deltaTheta < 0) deltaTheta += 2 * Math.PI

  const segments = Math.max(1, Math.ceil(Math.abs(deltaTheta) / (Math.PI / 2)))
  const step = deltaTheta / segments

  const pointAt = (t: number): [number, number] => [
    cx + rx * cosPhi * Math.cos(t) - ry * sinPhi * Math.sin(t),
    cy + rx * sinPhi * Math.cos(t) + ry * cosPhi * Math.sin(t),
  ]
  const derivativeAt = (t: number): [number, number] => [
    -rx * cosPhi * Math.sin(t) - ry * sinPhi * Math.cos(t),
    -rx * sinPhi * Math.sin(t) + ry * cosPhi * Math.cos(t),
  ]

  const alpha = (4 / 3) * Math.tan(step / 4)
  const out: number[][] = []
  for (let i = 0; i < segments; i++) {
    const t1 = theta1 + i * step
    const t2 = t1 + step
    const [p1x, p1y] = pointAt(t1)
    const [p2x, p2y] = pointAt(t2)
    const [d1x, d1y] = derivativeAt(t1)
    const [d2x, d2y] = derivativeAt(t2)
    out.push([
      p1x + alpha * d1x,
      p1y + alpha * d1y,
      p2x - alpha * d2x,
      p2y - alpha * d2y,
      p2x,
      p2y,
    ])
  }
  return out
}

const round = (n: number): number => Math.round(n * 1000) / 1000

/**
 * The same path with every arc command rewritten as cubic Béziers (or a line,
 * for a degenerate zero-radius arc). Arc-free path data is returned unchanged.
 */
export function convertArcsInPathData(d: string): string {
  if (!/[Aa]/.test(d)) return d

  let out = ''
  let curX = 0
  let curY = 0
  let startX = 0
  let startY = 0

  for (const [, command, rawArgs] of d.matchAll(PATH_COMMAND)) {
    const args = parseNumbers(rawArgs)
    const relative = command === command.toLowerCase()

    if (command === 'A' || command === 'a') {
      for (let i = 0; i + 6 < args.length; i += 7) {
        const [rx, ry, rotation, largeArc, sweep, ax, ay] = args.slice(i, i + 7)
        const endX = relative ? curX + ax : ax
        const endY = relative ? curY + ay : ay
        const cubics = arcToCubics(
          curX,
          curY,
          rx,
          ry,
          rotation,
          largeArc !== 0,
          sweep !== 0,
          endX,
          endY,
        )
        if (cubics.length === 0) {
          out += `L ${round(endX)} ${round(endY)} `
        } else {
          for (const c of cubics) out += `C ${c.map(round).join(' ')} `
        }
        curX = endX
        curY = endY
      }
      continue
    }

    // Non-arc commands pass through verbatim; only the current point is tracked,
    // so that a following arc knows where it starts from.
    out += command + rawArgs
    switch (command) {
      case 'M':
      case 'L':
      case 'T':
        curX = args[args.length - 2] ?? curX
        curY = args[args.length - 1] ?? curY
        if (command === 'M') {
          startX = curX
          startY = curY
        }
        break
      case 'm':
      case 'l':
      case 't':
        for (let i = 0; i + 1 < args.length; i += 2) {
          curX += args[i]
          curY += args[i + 1]
        }
        if (command === 'm') {
          startX = curX
          startY = curY
        }
        break
      case 'H':
        curX = args[args.length - 1] ?? curX
        break
      case 'h':
        for (const a of args) curX += a
        break
      case 'V':
        curY = args[args.length - 1] ?? curY
        break
      case 'v':
        for (const a of args) curY += a
        break
      case 'C':
      case 'S':
      case 'Q':
        curX = args[args.length - 2] ?? curX
        curY = args[args.length - 1] ?? curY
        break
      case 'c': {
        for (let i = 0; i + 5 < args.length; i += 6) {
          curX += args[i + 4]
          curY += args[i + 5]
        }
        break
      }
      case 's':
      case 'q': {
        for (let i = 0; i + 3 < args.length; i += 4) {
          curX += args[i + 2]
          curY += args[i + 3]
        }
        break
      }
      case 'Z':
      case 'z':
        curX = startX
        curY = startY
        break
      default:
        break
    }
  }

  return out.trimEnd()
}

// ── Sanitising ────────────────────────────────────────────────────────────

/**
 * `svgText` rewritten into the print server's subset: effect machinery removed,
 * unsupported elements and attributes dropped, arcs converted to cubics.
 *
 * The geometry and colours of the artwork survive; the drop shadow does not.
 */
export function sanitizePrintSvg(svgText: string): string {
  const root = parseSvg(svgText)
  if (root === null || (root.localName ?? root.nodeName) !== 'svg') return svgText

  // Figma expresses outside-only strokes with an inverted mask. Painting the
  // stroke underneath its matching fill preserves that outline in Tanger's
  // mask-free subset. Do this before the defs containing the masks disappear.
  for (const stroke of Array.from(root.querySelectorAll('path[mask]'))) {
    const id = /url\(#([^)]*)\)/.exec(stroke.getAttribute('mask') ?? '')?.[1]
    if (!id || !id.includes('outside')) continue
    const fill = Array.from(stroke.parentElement?.children ?? []).find(child => child !== stroke && child.localName === 'path' && child.getAttribute('d') === stroke.getAttribute('d') && child.getAttribute('fill') !== 'none')
    if (!fill) throw new Error('Outside outline has no matching fill')
    stroke.removeAttribute('mask')
    stroke.setAttribute('fill', 'none')
    fill.parentNode!.insertBefore(stroke, fill)
  }

  const visit = (el: Element) => {
    for (const child of Array.from(el.children)) {
      const name = child.localName ?? child.nodeName
      if (!ALLOWED_ELEMENTS.has(name)) {
        child.remove()
        continue
      }
      visit(child)
    }

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      if (name === 'xmlns') continue
      if (!ALLOWED_ATTRIBUTES.has(name)) {
        el.removeAttribute(attr.name)
        continue
      }
      if (COLOUR_ATTRIBUTES.has(name)) {
        const colour = normaliseColour(attr.value)
        if (colour === null) el.removeAttribute(attr.name)
        else if (colour !== attr.value) el.setAttribute(attr.name, colour)
      }
      if (name === 'd') {
        const path = convertArcsInPathData(attr.value)
        const fillRule = el.closest('[fill-rule]')?.getAttribute('fill-rule')
        el.setAttribute('d', fillRule === 'evenodd' ? nonZeroPathFromEvenOdd(path) : path)
      }
    }
  }

  // Before `visit`, which strips the `mask` attribute this has to read.
  underlayMaskedOutlines(root)
  visit(root)
  return new XMLSerializer().serializeToString(root)
}

/**
 * Moves every masked outline stroke underneath its siblings.
 *
 * The bare pin icons draw their white contrast outline as a second copy of the
 * glyph's path, stroked 2 units wide and masked so only the half OUTSIDE the
 * glyph shows. The subset has no masks, so the sanitiser drops it — and the
 * unmasked stroke then paints white over the glyph's whole edge, which on a
 * slender icon like the tower is the whole glyph: the pin printed as a white
 * blot. Painted underneath the fill instead, the inner half of the stroke is
 * covered by the glyph and the outer half shows, which is what the mask drew.
 *
 * Limited to elements that stroke and set no fill of their own, which is the
 * shape of that outline; a masked group or fill is left in its place.
 */
function underlayMaskedOutlines(root: Element): void {
  const outlines = Array.from(root.querySelectorAll('[mask]')).filter((el) => {
    const stroke = el.getAttribute('stroke')
    return stroke !== null && stroke !== 'none' && !el.hasAttribute('fill')
  })
  for (const el of outlines) {
    const parent = el.parentElement
    if (parent) parent.insertBefore(el, parent.firstElementChild)
  }
}

/**
 * A colour the print server accepts, or `null` when the value has no equivalent
 * (a gradient reference, `currentColor`, an `rgb()` function). Dropping the
 * attribute leaves the server's own default, which is a better outcome than a
 * rejected print job.
 */
function normaliseColour(value: string): string | null {
  const v = value.trim().toLowerCase()
  if (isSupportedColour(v)) return v
  return NAMED_COLOURS[v] ?? null
}

// ── Anchoring ─────────────────────────────────────────────────────────────

/**
 * `svgText` with its root viewBox extended downward by its own height, so the
 * drawing's bottom edge sits at the viewBox's centre.
 *
 * The print server anchors every symbol at the centre of its viewBox and has no
 * field to say otherwise, while a pin with a tip has to stand on it. Doubling
 * the viewBox's height below the artwork moves the centre to where the tip is;
 * the caller doubles the symbol's printed `height` to match, so the artwork
 * itself prints at the size asked for. The server places a label at
 * `height / 2` above the anchor, which on the doubled box is exactly the top of
 * the drawing — so the label stays where it was, just above the pin.
 *
 * A root `height` attribute is doubled as well so the document stays
 * self-consistent. A document without a usable viewBox is returned unchanged:
 * the sanitiser's violation check is what reports that, not this.
 */
export function standSvgOnBottomEdge(svgText: string): string {
  const root = parseSvg(svgText)
  if (root === null) return svgText
  const box = (root.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number)
  if (box.length !== 4 || box.some((n) => !Number.isFinite(n)) || box[3] <= 0) return svgText
  const [minX, minY, w, h] = box
  root.setAttribute('viewBox', `${minX} ${minY} ${w} ${h * 2}`)
  const height = Number(root.getAttribute('height'))
  if (Number.isFinite(height) && height > 0) root.setAttribute('height', String(height * 2))
  return new XMLSerializer().serializeToString(root)
}
